import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import API_BASE from '../config';
import CatalogService from '../services/catalog.service';
import { Link, useSearchParams } from 'react-router-dom';
import Pagination from './Pagination';
import ProductService from '../services/product.service';
import SubCatalogService from '../services/subcatalog.service';
import Spinner from './Spinner';
import { formatCurrency } from '../common/utils';
import { idFromSlug, productPath, slugify } from '../utils/slug';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';

// URL-driven filters (shareable / bookmarkable):
//   /catalog?catalog=<id|slug>        products in a catalog          e.g. ?catalog=electronics-1
//   /catalog?sub=<id|slug>            products in a sub-catalog      e.g. ?sub=phones-3
//   /catalog?q=<text>                 search product name
//   /catalog?category=<text>          search by catalog name
//   &page=<n>                         page number (any of the above)
// Filters combine, e.g. ?catalog=1&q=tv. With no filter, the first sub-catalog
// (or first catalog) is selected, matching the previous behaviour.
const Catalog = () => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalogs, setCatalogs] = useState([]);
  const [subCatalogs, setSubCatalogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [expanded, setExpanded] = useState({}); // { catalogId: bool }
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [refsLoaded, setRefsLoaded] = useState(false);
  const PAGE_SIZE = 10;

  // Current filters, read straight from the URL.
  const catalogParam = searchParams.get('catalog') || '';
  const subParam = searchParams.get('sub') || '';
  const qParam = (searchParams.get('q') || '').trim();
  const categoryParam = (searchParams.get('category') || '').trim();
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);
  const hasFilter = !!(catalogParam || subParam || qParam || categoryParam);
  const isSearching = !!(qParam || categoryParam);

  // Search inputs mirror the URL (and update when the URL changes, e.g. Back button).
  const [searchName, setSearchName] = useState(qParam);
  const [searchCategory, setSearchCategory] = useState(categoryParam);
  useEffect(() => setSearchName(qParam), [qParam]);
  useEffect(() => setSearchCategory(categoryParam), [categoryParam]);

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message || 'Something went wrong.';

  // Load menu data (public).
  useEffect(() => {
    Promise.allSettled([CatalogService.getAll(), SubCatalogService.getAll()]).then(([c, s]) => {
      if (c.status === 'fulfilled') setCatalogs(c.value.data || []);
      else toast.error(resolveMsg(c.reason));
      if (s.status === 'fulfilled') setSubCatalogs(s.value.data || []);
      else toast.error(resolveMsg(s.reason));
      setRefsLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subsByCatalog = useMemo(() => {
    const map = {};
    subCatalogs.forEach((s) => {
      const key = s.catalog_id;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [subCatalogs]);

  // Accept a plain id ("3") or a slug ending in the id ("phones-3").
  const findByParam = (list, param) => {
    if (!param) return null;
    const id = idFromSlug(param);
    return list.find((x) => x.id === id) || null;
  };
  const selectedCatalog = findByParam(catalogs, catalogParam);
  const selectedSub = findByParam(subCatalogs, subParam);

  // Link helpers. Clicking a catalog/sub-catalog clears any search and resets paging.
  const catalogLink = (c) => `/catalog?catalog=${slugify(c.name, c.id)}`;
  const subLink = (s) => `/catalog?sub=${slugify(s.name, s.id)}`;

  // No filter in the URL → pick the default (first sub-catalog of first catalog).
  useEffect(() => {
    if (!refsLoaded || hasFilter || catalogs.length === 0) return;
    const first = catalogs[0];
    const subs = subsByCatalog[first.id] || [];
    const next = subs.length > 0 ? { sub: slugify(subs[0].name, subs[0].id) } : { catalog: slugify(first.name, first.id) };
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refsLoaded, hasFilter, catalogs, subsByCatalog]);

  // Keep the selected catalog (or the parent of the selected sub-catalog) expanded.
  useEffect(() => {
    const openId = selectedSub ? selectedSub.catalog_id : selectedCatalog ? selectedCatalog.id : null;
    if (openId != null) setExpanded((e) => (e[openId] ? e : { ...e, [openId]: true }));
  }, [selectedSub, selectedCatalog]);

  // Fetch products whenever the URL filters change.
  const unknownFilter = refsLoaded && ((catalogParam && !selectedCatalog) || (subParam && !selectedSub));
  useEffect(() => {
    if (!refsLoaded || !hasFilter) return;
    if (unknownFilter) {
      setProducts([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }
    const params = { page, size: PAGE_SIZE };
    if (selectedCatalog) params.catalog_id = selectedCatalog.id;
    if (selectedSub) params.sub_catalog_id = selectedSub.id;
    if (qParam) params.q = qParam;
    if (categoryParam) params.category = categoryParam;
    let cancelled = false;
    setLoadingProducts(true);
    ProductService.getAll(params)
      .then((res) => {
        if (cancelled) return;
        const pages = res.data.totalPages || 1;
        // Page in the URL is past the end (e.g. stale bookmark) → jump to the last page.
        if (page > pages) {
          const next = new URLSearchParams(searchParams);
          if (pages > 1) next.set('page', String(pages));
          else next.delete('page');
          setSearchParams(next, { replace: true });
          return;
        }
        setProducts(res.data.items || []);
        setTotalPages(pages);
        setTotalItems(res.data.totalItems || 0);
      })
      .catch((err) => !cancelled && toast.error(resolveMsg(err)))
      .finally(() => !cancelled && setLoadingProducts(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refsLoaded, catalogParam, subParam, qParam, categoryParam, page, selectedCatalog && selectedCatalog.id, selectedSub && selectedSub.id]);

  // Update one or more URL params; null/'' removes a param.
  const updateParams = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => (v === null || v === undefined || v === '' ? next.delete(k) : next.set(k, String(v))));
    if (resetPage) next.delete('page');
    setSearchParams(next);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    updateParams({ page: p > 1 ? p : null }, { resetPage: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpand = (catalogId) => {
    setExpanded((e) => ({ ...e, [catalogId]: !e[catalogId] }));
  };

  // Search replaces the catalog/sub selection.
  const runSearch = (e) => {
    if (e) e.preventDefault();
    const q = searchName.trim();
    const category = searchCategory.trim();
    if (!q && !category) return;
    updateParams({ q, category, catalog: null, sub: null });
  };

  const clearSearch = () => {
    setSearchName('');
    setSearchCategory('');
    setSearchParams({}); // falls back to the default selection
  };

  // Heading for the right-hand panel.
  const heading = unknownFilter
    ? t('catalog.notFound', 'Category not found')
    : isSearching
    ? t('catalog.searchResults')
    : selectedSub
    ? t('catalog.productsInSub', { name: selectedSub.name })
    : selectedCatalog
    ? t('catalog.productsIn', { name: selectedCatalog.name })
    : t('catalog.selectPrompt');
  const showResults = hasFilter && refsLoaded;

  const imgSrc = (p) => {
    if (!p.image_url) return null;
    if (/^https?:\/\//i.test(p.image_url)) return p.image_url;
    // stored as "uploads/img/products/x.jpg" -> served at "/img/products/x.jpg"
    const clean = p.image_url.replace(/^uploads\//, '/');
    return API_BASE + (clean.startsWith('/') ? clean : '/' + clean);
  };

  const isCatalogSelected = (id) => !isSearching && !selectedSub && selectedCatalog && selectedCatalog.id === id;
  const isSubSelected = (id) => !isSearching && selectedSub && selectedSub.id === id;

  return (
    <div className='container mt-3'>
      <div className='row'>
        {/* LEFT MENU */}
        <div className='col-12 col-md-4 col-xl-3 mb-3'>
          {/* SEARCH BAR */}
          <form className='row g-2 mb-3' onSubmit={runSearch}>
            <div className='col-12 '>
              <input type='text' className='form-control' placeholder={t('catalog.searchNamePlaceholder')} value={searchName} onChange={(e) => setSearchName(e.target.value)} />
            </div>
            <div className='col-12 '>
              <input type='text' className='form-control' placeholder={t('catalog.searchCategoryPlaceholder')} value={searchCategory} onChange={(e) => setSearchCategory(e.target.value)} />
            </div>
            <div className='col-12 d-flex gap-2'>
              <button type='submit' className='btn btn-primary flex-grow-1' disabled={!searchName.trim() && !searchCategory.trim()}>
                {t('catalog.searchButton')}
              </button>
              {isSearching && (
                <button type='button' className='btn btn-outline-secondary' onClick={clearSearch}>
                  {t('catalog.clearSearch')}
                </button>
              )}
            </div>
          </form>
          <div className='bg-dark text-white rounded p-3 min-vh-75'>
            <h5 className='mb-3'>{t('catalog.title')}</h5>
            {catalogs.length === 0 ? (
              <p className='small'>{t('catalog.empty')}</p>
            ) : (
              <ul className='nav flex-column'>
                {catalogs.map((c) => (
                  <li key={c.id} className='nav-item mb-1'>
                    <div className='d-flex'>
                      <Link
                        to={catalogLink(c)}
                        className={'btn btn-sm flex-grow-1 text-start text-white ' + (isCatalogSelected(c.id) ? 'btn-primary' : 'btn-dark')}
                        aria-current={isCatalogSelected(c.id) ? 'page' : undefined}
                        onClick={() => setExpanded((e) => ({ ...e, [c.id]: true }))}
                      >
                        {c.name}
                      </Link>
                      <button
                        type='button'
                        className='btn btn-sm btn-dark text-white'
                        onClick={() => toggleExpand(c.id)}
                        aria-expanded={!!expanded[c.id]}
                        aria-label={c.name}
                      >
                        {expanded[c.id] ? '▾' : '▸'}
                      </button>
                    </div>

                    {expanded[c.id] && (
                      <ul className='nav flex-column ms-3 mt-1'>
                        {(subsByCatalog[c.id] || []).length === 0 ? (
                          <li className='small ps-2'>{t('catalog.noSubCatalogs')}</li>
                        ) : (
                          (subsByCatalog[c.id] || []).map((s) => (
                            <li key={s.id} className='nav-item'>
                              <Link
                                to={subLink(s)}
                                className={'btn btn-sm w-100 text-start text-white ' + (isSubSelected(s.id) ? 'btn-secondary' : 'btn-dark')}
                                aria-current={isSubSelected(s.id) ? 'page' : undefined}
                              >
                                ↳ {s.name}
                              </Link>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className='col-12 col-md-8 col-xl-9'>

          <h4>
            {heading}
            {showResults && !loadingProducts && totalItems > 0 && <small className='text-muted'> {t('catalog.totalCount', { count: totalItems })}</small>}
          </h4>

          {isSearching && (selectedCatalog || selectedSub) && (
            <p className='small text-muted mb-2'>{t('catalog.withinCategory', 'In: {{name}}', { name: (selectedSub || selectedCatalog).name })}</p>
          )}
          {!showResults ? (
            <Spinner />
          ) : unknownFilter ? (
            <p className='text-muted'>
              {t('catalog.notFoundHint', 'This category does not exist or was removed.')}{' '}
              <Link to='/catalog'>{t('catalog.viewAll', 'View all products')}</Link>
            </p>
          ) : loadingProducts ? (
            <Spinner label={t('catalog.loadingProducts')} />
          ) : products.length === 0 ? (
            <p className='text-muted'>{t('catalog.noProducts')}</p>
          ) : (
            <div className='row'>
              {products.map((p) => {
                const src = imgSrc(p);
                return (
                  <div className='col-12 col-sm-6 col-lg-4 mb-4' key={p.id}>
                    <Link to={productPath(p)} className='text-decoration-none text-reset'>
                      <div className='card h-100 product-card'>
                        {src ? (
                          <img
                            src={src}
                            alt={p.name}
                            className='card-img-top'
                            style={{ height: 180, objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className='bg-light d-flex align-items-center justify-content-center text-muted' style={{ height: 180 }}>
                            No image
                          </div>
                        )}
                        <div className='card-body'>
                          <h5 className='card-title'>{p.name}</h5>
                          <p className='card-text fw-bold text-primary mb-1'>{formatCurrency(p.price)}</p>
                          {p.description && (
                            <p className='card-text small text-muted'>
                              {p.description
                                .replace(/<[^>]*>/g, ' ')
                                .replace(/\s+/g, ' ')
                                .trim()
                                .slice(0, 120)}
                            </p>
                          )}
                          <div className='position-absolute bottom-0 end-0 me-2 mb-2'>
                            <button
                              type='button'
                              className='btn btn-light mt-2'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addToCart(p.id, 1).catch(() => {});
                              }}
                            >
                              <i className='icon-basket'></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          {!loadingProducts && showResults && !unknownFilter && <Pagination page={page} totalPages={totalPages} onChange={goToPage} />}
        </div>
      </div>
    </div>
  );
};

export default Catalog;
